import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { CategorizationRule } from "@/services/categorization-rules";
import { RootState } from "../root-reducer";

interface CategorizationRulesState {
  rules: CategorizationRule[];
  loading: boolean;
  error: string | null;
  updatingRules: string[]; // Track which rules are being updated
}

const initialState: CategorizationRulesState = {
  rules: [],
  loading: false,
  error: null,
  updatingRules: [],
};

// Thunks
export const fetchRulesAsync = createAsyncThunk<
  CategorizationRule[],
  { organizationId: string },
  { rejectValue: string }
>("categorizationRules/fetchRules", async ({ organizationId }, thunkAPI) => {
  try {
    const res = await fetch(
      `/api/categorization-rules?organizationId=${organizationId}`,
    );
    if (!res.ok) throw new Error("Failed to fetch rules");
    const data = await res.json();
    return data.rules;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.message || "Failed to fetch rules");
  }
});

export const createRuleAsync = createAsyncThunk<
  CategorizationRule,
  { rule: Partial<CategorizationRule>; organizationId: string },
  { rejectValue: string }
>(
  "categorizationRules/createRule",
  async ({ rule, organizationId }, thunkAPI) => {
    try {
      const res = await fetch(
        `/api/categorization-rules?organizationId=${organizationId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(rule),
        },
      );
      if (!res.ok) throw new Error("Failed to create rule");
      const data = await res.json();
      return data.rule;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.message || "Failed to create rule");
    }
  },
);

export const updateRuleAsync = createAsyncThunk<
  CategorizationRule,
  { rule: Partial<CategorizationRule>; organizationId: string },
  { rejectValue: string }
>(
  "categorizationRules/updateRule",
  async ({ rule, organizationId }, thunkAPI) => {
    try {
      const res = await fetch(
        `/api/categorization-rules/${rule.id}?organizationId=${organizationId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(rule),
        },
      );
      if (!res.ok) throw new Error("Failed to update rule");
      const data = await res.json();
      return data.rule;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.message || "Failed to update rule");
    }
  },
);

export const deleteRuleAsync = createAsyncThunk<
  string,
  { id: string; organizationId: string },
  { rejectValue: string }
>(
  "categorizationRules/deleteRule",
  async ({ id, organizationId }, thunkAPI) => {
    try {
      const res = await fetch(
        `/api/categorization-rules/${id}?organizationId=${organizationId}`,
        {
          method: "DELETE",
        },
      );
      if (!res.ok) throw new Error("Failed to delete rule");
      return id;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.message || "Failed to delete rule");
    }
  },
);

const categorizationRulesSlice = createSlice({
  name: "categorizationRules",
  initialState,
  reducers: {
    setRules(state, action: PayloadAction<CategorizationRule[]>) {
      state.rules = action.payload;
    },
    addRule(state, action: PayloadAction<CategorizationRule>) {
      state.rules.push(action.payload);
    },
    updateRule(state, action: PayloadAction<CategorizationRule>) {
      const idx = state.rules.findIndex((r) => r.id === action.payload.id);
      if (idx !== -1) state.rules[idx] = action.payload;
    },
    deleteRule(state, action: PayloadAction<string>) {
      state.rules = state.rules.filter((r) => r.id !== action.payload);
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    addUpdatingRule(state, action: PayloadAction<string>) {
      if (!state.updatingRules.includes(action.payload)) {
        state.updatingRules.push(action.payload);
      }
    },
    removeUpdatingRule(state, action: PayloadAction<string>) {
      state.updatingRules = state.updatingRules.filter(
        (id) => id !== action.payload,
      );
    },
    // Optimistic update for toggle
    optimisticToggleRule(
      state,
      action: PayloadAction<{ id: string; isActive: boolean }>,
    ) {
      const idx = state.rules.findIndex((r) => r.id === action.payload.id);
      if (idx !== -1) {
        state.rules[idx] = {
          ...state.rules[idx],
          isActive: action.payload.isActive,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRulesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRulesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = action.payload;
      })
      .addCase(fetchRulesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch rules";
      })
      .addCase(createRuleAsync.fulfilled, (state, action) => {
        state.rules.push(action.payload);
      })
      .addCase(updateRuleAsync.pending, (state, action) => {
        const ruleId = action.meta.arg.rule.id;
        if (ruleId && !state.updatingRules.includes(ruleId)) {
          state.updatingRules.push(ruleId);
        }
      })
      .addCase(updateRuleAsync.fulfilled, (state, action) => {
        const idx = state.rules.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) state.rules[idx] = action.payload;
        // Remove from updating rules
        state.updatingRules = state.updatingRules.filter(
          (id) => id !== action.payload.id,
        );
      })
      .addCase(updateRuleAsync.rejected, (state, action) => {
        const ruleId = action.meta.arg.rule.id;
        if (ruleId) {
          state.updatingRules = state.updatingRules.filter(
            (id) => id !== ruleId,
          );
        }
      })
      .addCase(deleteRuleAsync.fulfilled, (state, action) => {
        state.rules = state.rules.filter((r) => r.id !== action.payload);
      });
  },
});

export const {
  setRules,
  addRule,
  updateRule,
  deleteRule,
  setLoading,
  setError,
  addUpdatingRule,
  removeUpdatingRule,
  optimisticToggleRule,
} = categorizationRulesSlice.actions;

export default categorizationRulesSlice.reducer;
